import { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Image,
  InputGroup,
  Alert,
  Spinner // <-- Import Spinner for loading state
} from 'react-bootstrap';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { loginAPI } from '../services/authService'; // <-- Import the real API function

// --- (SVG Icon components are unchanged) ---
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
    <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="m10.79 12.912-1.614-1.615a3.5 3.5 0 0 1-4.474-4.474l-2.06-2.06C.938 6.278 0 8 0 8s3 5.5 8 5.5a7.029 7.029 0 0 0 2.79-.588zM5.21 3.088A7.028 7.028 0 0 1 8 2.5c5 0 8 5.5 8 5.5s-.939 1.721-2.641 3.238l-2.062-2.062a3.5 3.5 0 0 0-4.474-4.474L5.21 3.089z" />
    <path d="M5.525 7.646a2.5 2.5 0 0 0 2.829 2.829l-2.83-2.829zm4.95.708-2.829-2.83a2.5 2.5 0 0 1 2.829 2.829zm3.171 6-12-12 .708-.708 12 12-.708.708z" />
  </svg>
);
// ---------------------------------------------------


function Login() {
  // --- (Login logic state is unchanged) ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false); // <-- Loading state for API call
  const navigate = useNavigate();
  const location = useLocation();

  // --- Check for Session Expiry Param ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('sessionExpired') === 'true') {
      setSessionExpiredMsg('Your session has been expired.');
    }
  }, [location]);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email regex

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setApiError('');
    setSessionExpiredMsg(''); // Clear session msg if user tries to login

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // === CALL THE REAL API ===
      await loginAPI(email, password);

      // If successful, navigate to dashboard
      navigate('/');
    } catch (err) {
      // If API fails, show the error message from server
      console.log("err...", err)
      setApiError(typeof err === 'string' ? err : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear errors when typing
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
  };

  return (
    // === 1. Full-screen background container ===
    <div
      style={{
        backgroundImage: `url(/images/background_1.jpg)`, // Use your new image from /public
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      {/* === 2. "Focused" Login Card === */}
      <div
        style={{
          maxWidth: '450px',
          width: '100%',
          padding: '2.5rem',
          borderRadius: '1rem', // 16px
          // Frosted glass effect
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
        }}
      >
        {/* Logo */}
        <div className="text-center mb-4">
          <Image
            src="/images/logo.png" // Your original logo
            alt="AllWell Logo"
            style={{ height: '50px' }}
          />
        </div>

        {/* Headings */}
        <h2 className="fw-bold mb-2 text-center">Welcome!</h2>
        <p className="text-muted mb-4 text-center">Sign in to your account</p>

        {sessionExpiredMsg && (
          <Alert variant="warning">
            {sessionExpiredMsg}
          </Alert>
        )}

        {/* === 3. Form (unchanged logic) === */}
        {/* We removed the inner 'bg-white' box from the previous step */}
        <Form onSubmit={handleLogin}>

          {apiError && <Alert variant="danger">{apiError}</Alert>}

          <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={handleEmailChange}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formBasicPassword">
            <Form.Label>Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                isInvalid={!!errors.password}
              />
              <InputGroup.Text
                onClick={() => setShowPassword(!showPassword)}
                style={{ cursor: 'pointer' }}
              >
                {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {errors.password}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

          <div className="text-end mb-3">
            <Link to="/verify-email?forgotPassword" className="text-decoration-none" style={{ fontSize: '0.9rem' }}>
              Forgot Password?
            </Link>
          </div>

          <div className="d-grid">
            <Button variant="primary" type="submit" size="lg" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </div>
        </Form>

        {/* Register Link */}
        {/* <div className="text-center mt-4">
          <p className="text-muted">
            Don't have an account? <Link to="#register" className="fw-bold text-decoration-none">Register</Link>
          </p>
        </div> */}
      </div>
    </div>
  );
}

export default Login;